<template>
  <RolesTable
    :rows="roles"
    :active="(row, _) => route?.params?.id?.toString() === row.id"
    :loading="isLoading || isDeletingRole"
    :page="currentPage"
    :searchEnabled="true"
    :searchQuery="searchQuery"
    :has-more-items="hasNextPage"
    :removable="true"
    :editable="true"
    :level="'organisation'"
    @click-row="redirectToRole"
    @load="fetchNextPage"
    @search="search($event)"
    @create="showCreateRoleModal = true"
    @remove="
      (row: Role) => {
        activeRole = row;
        showDeleteRoleModal = true;
      }
    "
    @edit="
      (row: Role) => {
        activeRole = row;
        showUpdateRoleModal = true;
      }
    "
    @promote="doPromoteRole"
    @demote="doDemoteRole"
    @set-ranking="doSetRanking"
    :loading-ranking="isDemotingRole || isPromotingRole || isSettingRanking"
    :role-creation-permission="organisationRolePermissions.CREATE"
  />

  <!-- CREATE -->
  <MutationRoleModal
    v-if="showCreateRoleModal"
    @close="showCreateRoleModal = false"
    @mutate="onCreateRole"
    :loading="isCreatingRole"
    :organisation-id="props.model.id"
  />

  <!-- UPDATE -->
  <MutationRoleModal
    v-if="showUpdateRoleModal && activeRole"
    :role="activeRole"
    :loading="isUpdatingRole"
    @close="closeUpdateModal"
    @mutate="onUpdateRole"
    :organisation-id="props.model.id"
  />

  <InfuseConfirmModal
    v-if="showDeleteRoleModal"
    :action-title="t('iam.roles.header.delete_role')"
    :action-description="t('iam.roles.info.delete_role', { role: activeRole?.name || '' })"
    verification-text="delete"
    @confirm="onDeleteRole"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { Organisation, organisationRolePermissions } from '@infuse/api/iam/organisation';
import { CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import {
  useCreateOrganisationRole,
  useDeleteOrganisationRole,
  useDemoteOrganisationRole,
  usePromoteOrganisationRole,
  useSetOrganisationRoleRanking,
  useUpdateOrganisationRole
} from '@infuse/iam/organisation/mutations';
import { useOrganisationRoles } from '@infuse/iam/organisation/queries';
import { MutationRoleModal, RolesTable } from '@infuse/iam/role/ui';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

defineEmits(['reloadModel']);

const props = defineProps<{
  model: Organisation;
}>();

const {
  fetchParentId: fetchOrganisationId,
  roles,
  currentPage,
  isLoading,
  hasNextPage,
  fetchNextPage,
  searchQuery
} = useOrganisationRoles();

const activeRole = ref<Role | undefined>(undefined);

const { mutateAsync: createRole, isPending: isCreatingRole } = useCreateOrganisationRole();
const { mutateAsync: updateRole, isPending: isUpdatingRole } = useUpdateOrganisationRole();
const { mutateAsync: deleteRole, isPending: isDeletingRole } = useDeleteOrganisationRole();
const { mutateAsync: demoteRole, isPending: isDemotingRole } = useDemoteOrganisationRole();
const { mutateAsync: promoteRole, isPending: isPromotingRole } = usePromoteOrganisationRole();
const { mutateAsync: setRanking, isPending: isSettingRanking } = useSetOrganisationRoleRanking();

const router = useRouter();
const route = useRoute();

const showCreateRoleModal = ref(false);
const showUpdateRoleModal = ref(false);
const showDeleteRoleModal = ref(false);

watch(
  route,
  () => {
    fetchOrganisationId.value = route.params.id as string;
  },
  { immediate: true }
);

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'organisation.roles') {
    // only do this if the user is currently on this page and not some sub-route.
    return;
  }

  router.replace({
    name: 'organisation.roles',
    query: {
      search: searchQuery || undefined
    }
  });
}

function redirectToRole(row: Role): void {
  router.push({
    name: 'organisation.role.permissions',
    params: { id: props.model.id, roleId: row.id }
  });
}

async function onCreateRole(vars: { role: CreateRole }): Promise<void> {
  await createRole({ parentId: props.model.id, role: vars.role });
  showCreateRoleModal.value = false;
}

async function onDeleteRole(): Promise<void> {
  if (!activeRole.value) {
    return;
  }

  await deleteRole({ parentId: props.model.id, roleId: activeRole.value.id });
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteRoleModal.value = false;
  activeRole.value = undefined;
}

async function onUpdateRole(vars: { id?: string; role: UpdateRole | CreateRole }): Promise<void> {
  if (vars.id) {
    await updateRole({ parentId: props.model.id, roleId: vars.id, role: vars.role as UpdateRole });
    closeUpdateModal();
  }
}

function closeUpdateModal() {
  showUpdateRoleModal.value = false;
  activeRole.value = undefined;
}

async function doDemoteRole(role: Role) {
  await demoteRole({ parentId: props.model.id, roleId: role.id });
}

async function doPromoteRole(role: Role) {
  await promoteRole({ parentId: props.model.id, roleId: role.id });
}

async function doSetRanking(role: Role, ranking: number): Promise<void> {
  await setRanking({ parentId: props.model.id, roleId: role.id, ranking });
}
</script>
